/**
 * VendorDocumentService
 * Service for managing vendor documents (composite key)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for vendor document operations with composite key (vendorId + documentTypeId)
 */
export class VendorDocumentService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorDocumentService.DEFAULT_SERVICE_PATH,
            basePath: VendorDocumentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all vendor documents
     * @returns Array of vendor documents
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor documents with full response
     * @returns ResponseData with array of vendor documents
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE ID ====================
    /**
     * Get vendor document by composite key
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @returns The vendor document
     */
    async getById(vendorId, documentTypeId) {
        const response = await this.getByIdRaw(vendorId, documentTypeId);
        if (!response.data) {
            throw new Error(`VendorDocument not found: vendorId=${vendorId}, documentTypeId=${documentTypeId}`);
        }
        return response.data;
    }
    /**
     * Get vendor document by composite key with full response
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @returns ResponseData with vendor document
     */
    getByIdRaw(vendorId, documentTypeId) {
        const url = this.buildUrl(`${vendorId}/${documentTypeId}`);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new vendor document
     * @param data - Vendor document data
     * @returns The created vendor document
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create VendorDocument');
        }
        return response.data;
    }
    /**
     * Create a new vendor document with full response
     * @param data - Vendor document data
     * @returns ResponseData with created vendor document
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update a vendor document
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @param data - Updated vendor document data
     * @returns The updated vendor document
     */
    async update(vendorId, documentTypeId, data) {
        const response = await this.updateRaw(vendorId, documentTypeId, data);
        if (!response.data) {
            throw new Error(`Failed to update VendorDocument: vendorId=${vendorId}, documentTypeId=${documentTypeId}`);
        }
        return response.data;
    }
    /**
     * Update a vendor document with full response
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @param data - Updated vendor document data
     * @returns ResponseData with updated vendor document
     */
    updateRaw(vendorId, documentTypeId, data) {
        const url = this.buildUrl(`${vendorId}/${documentTypeId}`);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a vendor document
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @returns True if deleted successfully
     */
    async delete(vendorId, documentTypeId) {
        const response = await this.deleteRaw(vendorId, documentTypeId);
        return response.success;
    }
    /**
     * Delete a vendor document with full response
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(vendorId, documentTypeId) {
        const url = this.buildUrl(`${vendorId}/${documentTypeId}`);
        return this.http.delete(url);
    }
    // ==================== CHANGE STATUS ====================
    /**
     * Change document verification status
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @param isVerified - Whether to verify (approve) the document
     * @param reason - Reason for rejection (required when isVerified = false)
     * @returns The updated vendor document
     */
    async changeStatus(vendorId, documentTypeId, isVerified, reason) {
        const response = await this.changeStatusRaw(vendorId, documentTypeId, isVerified, reason);
        if (!response.data) {
            throw new Error(`Failed to change status for document ${vendorId}:${documentTypeId}`);
        }
        return response.data;
    }
    /**
     * Change document verification status with full response
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @param isVerified - Whether to verify (approve) the document
     * @param reason - Reason for rejection (required when isVerified = false)
     * @returns ResponseData with updated vendor document
     */
    changeStatusRaw(vendorId, documentTypeId, isVerified, reason) {
        const url = this.buildUrl(`${vendorId}/${documentTypeId}/change-status`);
        const request = {
            vendorID: vendorId,
            documentTypeID: documentTypeId,
            isVerified,
            reason: reason ?? null,
        };
        return this.http.post(url, request);
    }
    // ==================== CUSTOM QUERIES ====================
    /**
     * Get all documents for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor document views
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all documents for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor document views
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
    // ==================== MODEL VIEW QUERIES ====================
    /**
     * Get all vendor documents as model views
     * @returns Array of vendor document model views
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor documents as model views with full response
     * @returns ResponseData with array of vendor document model views
     */
    getViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get vendor document model view by composite key
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @returns The vendor document model view
     */
    async getView(vendorId, documentTypeId) {
        const response = await this.getViewRaw(vendorId, documentTypeId);
        if (!response.data) {
            throw new Error(`VendorDocument view not found: vendorId=${vendorId}, documentTypeId=${documentTypeId}`);
        }
        return response.data;
    }
    /**
     * Get vendor document model view by composite key with full response
     * @param vendorId - Vendor ID
     * @param documentTypeId - Document type ID
     * @returns ResponseData with vendor document model view
     */
    getViewRaw(vendorId, documentTypeId) {
        const url = this.buildUrl(`${vendorId}/${documentTypeId}/view`);
        return this.http.get(url);
    }
    /**
     * Get all document model views for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor document model views
     */
    async getViewsByVendor(vendorId) {
        const response = await this.getViewsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all document model views for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor document model views
     */
    getViewsByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}/views`);
        return this.http.get(url);
    }
}
VendorDocumentService.DEFAULT_SERVICE_PATH = 'vendors';
VendorDocumentService.BASE_PATH = 'api/vendor-documents';
//# sourceMappingURL=VendorDocumentService.js.map